/* ===========================================================================
   assets/int-frequency.js
   The Intention Frequency player's ONE delegated handler. Loaded once from
   <head> with defer, via snippets/int-frequency-runtime.liquid.
   Nils 2026-09-11.

   NOTHING IS FETCHED UNTIL A PRESS. Every <audio> the player writes is
   preload="none", so a page with thirty five players downloads zero audio on
   load. That is also why the level is driven from playback position and not
   from a Web Audio analyser: an analyser needs a MediaElementSource per
   element, and a cross origin source without CORS feeds it silence, which
   would mute the very file this component exists to play.

   ONE SOUND AT A TIME. Pressing a second player stops the first. The handler is
   delegated on document, so a player added by Searchanise, by the theme editor,
   or by any later section works with no re-binding.

   THE LEVEL IS KEYED TO THE EARTH TEMPO, 78.30 BPM, which is 1.305 Hz. It is a
   decorative level, not a measurement, and it moves at the tempo of the file it
   sits on rather than at an arbitrary speed. Reduced motion holds it still.

   THE PRESS AND THE SOUND ARE TWO MOMENTS. Measured live on the draft
   2026-09-11: a signature is about 394 KB and preload is "none", so the first
   press on a cold cache has to fetch the whole file first. Playback position
   stayed at zero for over a second while the level, driven from that position,
   sat frozen mid pattern. So the level now waits for the `playing` event, and
   `is-wait` carries the interval: pause icon showing, level at hairline.
   =========================================================================== */

(function(){
  if (window.__intFreqPlayer) return;
  window.__intFreqPlayer = true;

  /* 78.30 BPM, the Earth tempo, expressed in Hz. 7.83 x 60 / 6. */
  var TEMPO = 1.305;
  var REST = 14, LIFT = 78;

  var current = null, raf = null;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function flat(el){
    var b = el.querySelectorAll('.ifp__bars i');
    for (var i = 0; i < b.length; i++) b[i].style.height = REST + '%';
  }

  function stop(){
    if (!current) return;
    var a = current.querySelector('audio');
    if (a) { a.pause(); try { a.currentTime = 0; } catch (err) {} }
    current.classList.remove('is-on');
    current.classList.remove('is-wait');
    var btn = current.querySelector('.ifp__btn');
    if (btn) { btn.setAttribute('aria-pressed', 'false'); btn.removeAttribute('aria-busy'); }
    flat(current);
    current = null;
  }

  /* The bars breathe at the Earth tempo, each one a little behind the last,
     rising over the first second and settling over the last. Decorative. */
  function tick(){
    if (!current) { raf = null; return; }
    /* nothing is claimed until the sound is actually running */
    if (!reduce && current.classList.contains('is-on')) {
      var a = current.querySelector('audio');
      var t = a ? a.currentTime : 0;
      var d = (a && isFinite(a.duration)) ? a.duration : 0;
      var env = Math.min(1, 0.30 + t / 1.1);
      if (d && d - t < 1.4) env *= Math.max(0, (d - t) / 1.4);
      var b = current.querySelectorAll('.ifp__bars i');
      for (var i = 0; i < b.length; i++) {
        var v = REST + LIFT * env * Math.abs(Math.sin(Math.PI * TEMPO * t + i * 0.55));
        b[i].style.height = v.toFixed(1) + '%';
      }
    }
    raf = requestAnimationFrame(tick);
  }

  document.addEventListener('click', function(e){
    var t = e.target;
    if (!t || !t.closest) return;

    /* ── the download ────────────────────────────────────────────────────
       The files are on the Shopify CDN, a different origin, and a browser
       ignores the `download` attribute across origins: the link would play
       the mp3 in a tab instead of saving it. So fetch it as a blob, which
       the CDN allows, and save that. If the fetch is refused for any
       reason the link is released to behave normally. */
    var dl = t.closest('.ifp__dl');
    if (dl) {
      if (dl.getAttribute('data-passthrough')) return;
      e.preventDefault();
      fetch(dl.href).then(function(r){
        if (!r.ok) throw new Error(r.status);
        return r.blob();
      }).then(function(blob){
        var u = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = u;
        a.download = dl.getAttribute('data-file') || 'intention-frequency.mp3';
        document.body.appendChild(a);
        a.click();
        a.parentNode.removeChild(a);
        setTimeout(function(){ URL.revokeObjectURL(u); }, 8000);
      }).catch(function(){
        dl.setAttribute('data-passthrough', '1');
        dl.click();
      });
      return;
    }

    var btn = t.closest('.ifp__btn');
    /* 🐛 INT 2026-09-13: ON A CARD THE LEVEL IS NOT INSIDE THE BUTTON, AND A PRESS
       ON IT WENT STRAIGHT THROUGH TO THE CARD'S LINK. Nils: "Pressing the finished
       sound of the frequency takes me to PDP. Should it? It should come back."
       It should not. While a sound runs the name gives way to the level, so the
       level is sitting exactly where the words were a second ago and it is the
       obvious thing to press. It is a sibling of the button, so the handler below
       never saw it and the card link won.
       ⚑ On the CARD size only, any press inside the player resolves to its button.
       Scoped to .ifp--card on purpose: the full and standard players carry a
       download link inside the same wrapper, and that link must keep working. */
    if (!btn) {
      var card = t.closest('.ifp--card');
      if (card) btn = card.querySelector('.ifp__btn');
    }
    if (!btn) return;
    var el = btn.closest('.ifp');
    if (!el) return;
    /* INT 2026-09-13: on a product card the player sits INSIDE the card's own
       link, so a press would start the sound AND open the product. The press
       belongs to the player, so the link's default is cancelled here rather
       than in either of the two card renderers. The event still bubbles, which
       is what a keyboard Enter or Space needs, and nothing is stopped for any
       other surface: a player that is not inside a link is untouched. */
    if (btn.closest('a')) e.preventDefault();
    var a = el.querySelector('audio');
    if (!a) return;

    if (current === el) { stop(); return; }
    stop();

    current = el;
    /* the press is acknowledged at once, the level only when the sound starts */
    el.classList.add('is-wait');
    btn.setAttribute('aria-pressed', 'true');
    btn.setAttribute('aria-busy', 'true');
    try { a.currentTime = 0; } catch (err) {}
    a.onplaying = function(){
      if (current !== el) return;
      el.classList.remove('is-wait');
      el.classList.add('is-on');
      btn.removeAttribute('aria-busy');
      if (!raf) raf = requestAnimationFrame(tick);
    };
    a.onended = function(){ stop(); };
    a.onerror = function(){ stop(); };
    var p = a.play();
    if (p && p.catch) p.catch(function(){ stop(); });
  });
})();
